"use client";

/**
 * fund-risk-metrics.tsx
 *
 * Compact 2×3 (3×2 on desktop) mini-grid of risk ratios: Sharpe, Sortino,
 * Beta, Std deviation, Volatility scale (X/10), R-squared. Sharpe/Sortino
 * are color-coded by strength. Null metrics render as "—".
 *
 * Used in: src/app/funds/[slug]/page.tsx (Risk Analysis section — PRO tier)
 */

export interface FundRiskMetricsProps {
  sharpeRatio: number | null;
  sortinoRatio: number | null;
  beta: number | null;
  standardDeviation: number | null; // already as percent (e.g. 14.2)
  volatilityScale: number | null; // 1-10 scale
  rSquared: number | null;
}

interface Tile {
  label: string;
  value: string;
  color: string;
  hint?: string;
}

/** Sharpe/Sortino color code: >1 green, 0.5-1 blue, <0.5 amber, null gray */
function ratioColor(v: number | null): string {
  if (v === null || v === undefined || !isFinite(v)) return "#64748B";
  if (v >= 1.0) return "#10B981";
  if (v >= 0.5) return "#3B82F6";
  return "#F59E0B";
}

function formatNumber(v: number | null, digits = 2): string {
  if (v === null || v === undefined || !isFinite(v)) return "-";
  return v.toFixed(digits);
}

export function FundRiskMetrics({
  sharpeRatio,
  sortinoRatio,
  beta,
  standardDeviation,
  volatilityScale,
  rSquared,
}: FundRiskMetricsProps) {
  const allNull =
    sharpeRatio === null &&
    sortinoRatio === null &&
    beta === null &&
    standardDeviation === null &&
    volatilityScale === null &&
    rSquared === null;

  if (allNull) return null; // hide entire grid if zero data

  const tiles: Tile[] = [
    {
      label: "Sharpe",
      value: formatNumber(sharpeRatio),
      color: ratioColor(sharpeRatio),
      hint: "risk-adjusted return",
    },
    {
      label: "Sortino",
      value: formatNumber(sortinoRatio),
      color: ratioColor(sortinoRatio),
      hint: "downside-adjusted",
    },
    {
      label: "Beta",
      value: formatNumber(beta),
      color: "#94A3B8",
      hint: "market sensitivity",
    },
    {
      label: "Std deviation",
      value:
        standardDeviation !== null && standardDeviation !== undefined
          ? `${standardDeviation.toFixed(1)}%`
          : "-",
      color: "#94A3B8",
      hint: "annualised",
    },
    {
      label: "Volatility",
      value:
        volatilityScale !== null && volatilityScale !== undefined
          ? `${volatilityScale}/10`
          : "-",
      color: "#94A3B8",
      hint: "relative scale",
    },
    {
      label: "R-squared",
      value: formatNumber(rSquared),
      color: "#94A3B8",
      hint: "benchmark fit",
    },
  ];

  return (
    <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
      <div className="text-foreground text-sm font-serif mb-3">Risk metrics</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {tiles.map((t) => (
          <div
            key={t.label}
            className="bg-surface/60 rounded-lg p-3 border border-surface-light/10"
          >
            <div className="text-muted-foreground text-[10px] uppercase tracking-wide mb-1">
              {t.label}
            </div>
            <div
              className="text-[18px] font-bold font-tabular leading-none"
              style={{ color: t.color }}
            >
              {t.value}
            </div>
            {t.hint && (
              <div className="text-dim text-[10px] mt-1">{t.hint}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
